/**
 * What the become-a-provider form sends, and what it refuses — `W2-T05` §2, §3.5.
 *
 * Pure, like `features/jobs/posting.ts`: `FormData` in, either the contract's `ProviderProfileCreate`
 * or a translation key per field out. The rules restate `ProviderProfileCreateSchema`'s bounds so a
 * person is told which field is wrong in their own words before anything is sent; the API parses
 * again, and its answer is the one that counts.
 *
 * Two conversions a person should not have to do: the radius is typed in **kilometres** and sent in
 * metres, and the rate in **euros** and sent in cents. No coordinates — the server places the postal
 * code (§3.4).
 */
import {
  PROVIDER_CATEGORY_MAX,
  ProviderKindSchema,
  SERVICE_RADIUS_MAX_METRES,
  type ProviderProfileCreate,
} from '@marketplace/contracts';
import { readAddress } from '../../shared/address.js';
import { eurosToCents } from '../jobs/posting.js';

export type ProviderFormField =
  | 'displayName'
  | 'kind'
  | 'categories'
  | 'bio'
  | 'line1'
  | 'postalCode'
  | 'city'
  | 'province'
  | 'radius'
  | 'rate';

export type ProviderFormResult =
  | { ok: true; input: ProviderProfileCreate }
  | { ok: false; errors: Partial<Record<ProviderFormField, string>> };

/** What the radius field starts at: the seeded provider's, and a sensible town-and-around. */
export const DEFAULT_RADIUS_KM = 15;
const RADIUS_MAX_KM = SERVICE_RADIUS_MAX_METRES / 1000;

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

export function parseProviderForm(form: FormData): ProviderFormResult {
  const errors: Partial<Record<ProviderFormField, string>> = {};

  const displayName = text(form, 'displayName');
  const kind = ProviderKindSchema.safeParse(text(form, 'kind'));
  const categories = form.getAll('categories').filter((v): v is string => typeof v === 'string');
  const bio = text(form, 'bio');
  const { address, errors: addressErrors } = readAddress(form, 'proForm');
  const radiusTyped = text(form, 'radiusKm');
  const rateTyped = text(form, 'rate');

  if (displayName.length < 2) errors.displayName = 'proForm.error.displayName';
  else if (displayName.length > 120) errors.displayName = 'proForm.error.displayNameLong';
  if (!kind.success) errors.kind = 'proForm.error.kind';
  if (categories.length === 0) errors.categories = 'proForm.error.categories';
  else if (categories.length > PROVIDER_CATEGORY_MAX)
    errors.categories = 'proForm.error.categoriesMany';
  if (bio.length > 2000) errors.bio = 'proForm.error.bioLong';
  Object.assign(errors, addressErrors);

  const radiusKm = /^\d{1,3}$/.test(radiusTyped) ? Number(radiusTyped) : undefined;
  if (radiusKm === undefined || radiusKm < 1 || radiusKm > RADIUS_MAX_KM) {
    errors.radius = 'proForm.error.radius';
  }

  // Blank is **quote-only** — a rate, not a missing field (`ProviderProfileWriteSchema`).
  const rateCents = rateTyped === '' ? null : eurosToCents(rateTyped);
  if (rateCents === undefined) errors.rate = 'proForm.error.rate';

  if (Object.keys(errors).length > 0 || !kind.success || radiusKm === undefined) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    input: {
      displayName,
      kind: kind.data,
      bio: bio === '' ? null : bio,
      categories,
      serviceRadiusMetres: radiusKm * 1000,
      hourlyRateCents: rateCents ?? null,
      baseAddress: address,
    },
  };
}
